import { useContext, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import { embedDashboard } from "@superset-ui/embedded-sdk";
import { AppLogoIcon } from "components/svg-icons";
import { ProjectContext } from "context/ProjectProvider";
import { client } from "services/axios";
import { BASE_URL } from "config/config";
import { supersetEnv } from "utils/utils";
import useAuditEventLog from "entity-app/utils/export-audit-log";
import { DASHBOARD_EXPORT_EVENT_TYPES } from "constants/index";
import ExportPdfPrintModal from "pages/print-view-layout/Export-Pdf-Print-Modal";

const DASHBOARD_TITLES: Record<number, string> = {
  7: "Material Progress",
  9: "Material Readiness",
  11: "Submittal Progress"
};

const formatExportedAt = (date: Date) => {
  const month = date.getMonth() + 1;
  const day = date.getDate();
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "pm" : "am";
  hours %= 12;
  if (hours === 0) hours = 12;

  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const absOffset = Math.abs(offsetMinutes);
  const offsetHours = Math.floor(absOffset / 60);
  const offsetMins = (absOffset % 60).toString().padStart(2, "0");

  return `${month}/${day} at ${hours}:${minutes}${ampm} GMT${sign}${offsetHours}:${offsetMins}`;
};

export default function GCProjectDashboardExport() {
  const location = useLocation();
  const { projectToken, gqlClientForProject, projectDetails } =
    useContext(ProjectContext);
  const { saveAuditLogEvent } = useAuditEventLog(gqlClientForProject);

  const dashboardNo = Number(
    new URLSearchParams(location.search).get("dashboard_no")
  );

  const exportedAt = useMemo(() => formatExportedAt(new Date()), []);

  const handlePrint = () => {
    ExportPdfPrintModal({
      content: (
        <div>
          <p style={{ marginBottom: 8, fontWeight: 600 }}>
            To print exactly what appears on screen, we recommend:
          </p>
          <ul style={{ marginBottom: 12, paddingLeft: 20 }}>
            <li>
              <strong>Resolution:</strong> 1920×1080
            </li>
            <li>
              <strong>Display scaling:</strong> 100%
            </li>
            <li>
              <strong>Browser zoom:</strong> 100%
            </li>
          </ul>
          <p style={{ marginBottom: 8, fontWeight: 600 }}>Print settings:</p>
          <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
            <li>
              <strong>Orientation:</strong> Landscape
            </li>
            <li>
              <strong>Format:</strong> Letter
            </li>
            <li>
              <strong>Scale:</strong> 50%
            </li>
          </ul>
        </div>
      ),
      onProceed: () => {
        const eventType = DASHBOARD_EXPORT_EVENT_TYPES[dashboardNo];
        if (eventType) {
          saveAuditLogEvent({
            eventType,
            featureType: null,
            filename: `${eventType}.pdf`
          });
        }
        window.print();
      }
    });
  };

  useEffect(() => {
    if (!projectToken || !dashboardNo) return;

    const container = document.getElementById(
      "project-export-dashboard-container"
    );
    const loadDashboard = async () => {
      try {
        if (!container) {
          console.error("Dashboard container not found.");
          return;
        }

        const tokens = await client.post(
          `${BASE_URL}/superset/dashboard`,
          {
            dashboard_no: dashboardNo
          },
          {
            headers: {
              Authorization: `Bearer ${projectToken}`,
              "Content-Type": "application/json"
            }
          }
        );

        embedDashboard({
          id: tokens.data.dashboard_id,
          supersetDomain: supersetEnv(),
          mountPoint: container,
          fetchGuestToken: () => Promise.resolve(tokens.data.token),
          dashboardUiConfig: {
            hideTitle: true,
            hideChartControls: false,
            hideTab: true
          }
        });
      } catch (error) {
        console.error("Error posting data:", error);
        throw error;
      }
    };

    if (!container?.hasChildNodes()) {
      loadDashboard();
    }
  }, [projectToken, dashboardNo]);

  return (
    <div className="p-3">
      <div className="flex gap-6 px-1 pb-4 pt-1">
        <div className="w-[120px] flex">
          <AppLogoIcon />
        </div>
        <div className="text-xl font-bold">
          {projectDetails?.name} - {DASHBOARD_TITLES[dashboardNo]} - Exported
          from ConstructivIQ on {exportedAt}
        </div>
      </div>
      <div
        id="project-export-dashboard-container"
        className="w-full h-[calc(100vh-72px)] hideScroll"
      />
      <Button
        type="primary"
        shape="circle"
        size="large"
        icon={<PrinterOutlined />}
        aria-label="Print"
        onClick={handlePrint}
        className="print:hidden fixed bottom-6 right-6 shadow-lg z-50"
      />
    </div>
  );
}
